import { ResumeSubFlow } from '@/components/layouts/ResumeSubFlow';
import { StepLayout } from '@/components/layouts/StepLayout';
import { Providers } from '@/components/providers';
import { checkMaintenance } from '@/lib/loaders/checkMaintenance';
import { handleCheckoutConfirmation } from '@/lib/loaders/checkoutConfirmation';
import { handleResumeSubFlow } from '@/lib/loaders/resumeSubFlow';
import { regularSteps } from '@/lib/utils/steps';
import { Flows, StepGroups, SubflowSteps, type Step } from '@/types/steps';
import { NEXT_PUBLIC_GOOGLE_RECAPTCHA_SITE_KEY, WEB_URL } from '@petplate/settings';
import ThemeProvider from '@petplate/ui/components/ThemeProvider';
import filter from 'lodash/filter';
import map from 'lodash/map';
import { GoogleReCaptchaProvider } from 'react-google-recaptcha-v3';
import {
  Navigate,
  RouterProvider,
  createBrowserRouter,
  useLocation,
  type RouteObject
} from 'react-router-dom';
import { SubscriptionFlow } from '../components/layouts/SubscriptionFlow';
import { ErrorBoundary } from './error';

const RouteComponents = {
  [SubflowSteps.Intro]: () => import('./subscription/(intro)/intro'),
  [SubflowSteps.Name]: () => import('./subscription/(my-pup)/name'),
  [SubflowSteps.Email]: () => import('./subscription/(my-pup)/email'),
  [SubflowSteps.GenderBreed]: () => import('./subscription/(my-pup)/gender-breed'),
  [SubflowSteps.Age]: () => import('./subscription/(my-pup)/age'),
  [SubflowSteps.WeightActivity]: () => import('./subscription/(my-pup)/weight-activity'),
  [SubflowSteps.Waistline]: () => import('./subscription/(my-pup)/waistline'),
  [SubflowSteps.SensitivitiesGoals]: () => import('./subscription/(my-pup)/sensitivities-goals'),
  [SubflowSteps.PlanPreferences]: () => import('./subscription/(my-pup)/plan-preferences'),
  [SubflowSteps.CalculatingPlan]: () => import('./subscription/(other)/calculating-plan'),
  [SubflowSteps.Plans]: () => import('./subscription/(products)/plans'),
  [SubflowSteps.PlanConfirmation]: () => import('./subscription/(products)/plan-confirmation')
};

// component to trigger the redirect to the web app root
const RedirectWebApp = () => {
  const { pathname } = useLocation();
  window.location.href = `${WEB_URL}${pathname}`;
  return null;
};

/**
 * Creates a route for a subflow step
 *
 * @param step the step to create a route for
 * @returns the route configuration for the given step, with a loader and a component
 */
const mapSubRoutes = (step: Step) =>
  ({
    path: step.fullPath,
    // lazy load each step's component
    lazy: RouteComponents[step.path]
  } as RouteObject);

/**
 * Creates all the available routes for a given flow
 *
 * @param flow the flow to create routes for
 * @param flowSteps the steps of the given flow
 * @returns the routes for each step of the given flow and wraps them with a StepsProvider to access from anywhere within
 */
const mapFlowRoutes = (flow: Flows, flowSteps: Step[]) => {
  const firstRoute = flowSteps[0].fullPath;
  return [
    {
      element: <SubscriptionFlow flow={flow} steps={flowSteps} />,
      children: [
        ...map(Object.values(StepGroups), (view) => ({
          element: <StepLayout view={view} />,
          children: map(filter(flowSteps, { group: view }), mapSubRoutes)
        })),
        {
          path: `/${flow}/*`,
          element: <Navigate replace to={`${firstRoute}${window.location.search}`} />
        }
      ]
    }
  ];
};

/**
 * ROUTER DEFINITION FOR THE SUBFLOW
 *
 * Contains all paths, including all existing flows and each step for those flows
 */
const router = createBrowserRouter([
  {
    element: <Providers />,
    errorElement: <ErrorBoundary />,
    loader: checkMaintenance,
    children: [
      // Subflow routes
      {
        path: '/subscription/confirmation',
        loader: handleCheckoutConfirmation
      },
      {
        path: '/subscription/resume',
        loader: handleResumeSubFlow,
        element: <ResumeSubFlow />
      },
      ...mapFlowRoutes(Flows.Regular, regularSteps),
      // wildcard to redirect back to web app
      {
        path: '*',
        element: <RedirectWebApp />
      }
    ]
  }
]);

export const SubflowApp = () => (
  <GoogleReCaptchaProvider useEnterprise reCaptchaKey={NEXT_PUBLIC_GOOGLE_RECAPTCHA_SITE_KEY}>
    <ThemeProvider>
      <RouterProvider router={router} />
    </ThemeProvider>
  </GoogleReCaptchaProvider>
);
